import React, { useEffect, useState } from "react";
import FaiTypography from "MuiComponents/FaiTypography";
import FaiBox from "MuiComponents/FaiBox";
import { Box, Card, Checkbox, CircularProgress, Tooltip } from "@mui/material";
import { toast } from "sonner";
import StatisticsCard from "./components/StatisticCard";
import PageHeader from "screens/OnBoarding/components/PageHeader";
import SearchInput from "components/SearchInput";
import dataTableData from "./data/dataTableData";
import CustomerDetails from "./components/CustomerDetails";
import Axios from "services/api/config";
import { getCustomerListURL } from "services/api/routes/Sale";
import { getStaffURL, grantHelpDeskURL, revokeHelpDeskURL } from "services/api/routes/Staff";
import FaiDataGrid from "components/DataGrid/FaiDataGrid";
import { GridPaginationModel, GridRowParams } from "@mui/x-data-grid";

interface PaginationModel {
	page: number;
	pageSize: number;
}

interface CustomerData {
	data: any[];
	totalElements: number;
	totalPages: number;
}

interface Customer {
	id: string | number;
	[key: string]: any;
}

const Customer: React.FC = (props) => {
	const [customerDetails, setCustomer] = useState<Customer | undefined>();
	const [customersList, setCustomerList] = useState<Customer[]>([]);
	const [loading, setLoading] = useState(false);
	const [paginationModel, setPaginationModel] = useState<PaginationModel>({
		page: 0,
		pageSize: 10,
	});
	const [rowCount, setRowCount] = useState(0);
	const [search, setSearch] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");
	// Customers who have help-desk access (by customer id), and the row being changed.
	const [helpDesk, setHelpDesk] = useState<Set<string>>(new Set());
	const [savingId, setSavingId] = useState<string | null>(null);

	const loadHelpDesk = async () => {
		try {
			const { data } = await Axios({ ...getStaffURL });
			setHelpDesk(new Set((data?.data || []).map((s: any) => s.customerId).filter(Boolean)));
		} catch (error) {
			console.error(error);
		}
	};

	useEffect(() => {
		loadHelpDesk();
	}, []);

	// Tick = give help-desk access (they sign in to this dashboard with their own
	// Styola email + password); untick = take it away.
	const toggleHelpDesk = async (row: any, give: boolean) => {
		const id = String(row.id);
		setSavingId(id);
		try {
			await Axios({ ...(give ? grantHelpDeskURL(id) : revokeHelpDeskURL(id)) });
			setHelpDesk((prev) => {
				const next = new Set(prev);
				if (give) next.add(id);
				else next.delete(id);
				return next;
			});
			toast.success(give ? `${row.name || row.email} can now sign in to the help desk` : `Help-desk access removed for ${row.name || row.email}`);
		} catch (e: any) {
			toast.error(e?.response?.data?.errors?.[0] || e?.response?.data?.message || "Could not change help-desk access");
		} finally {
			setSavingId(null);
		}
	};

	const columns = [
		...dataTableData.columns,
		{
			headerName: "Help desk",
			field: "helpDesk",
			sortable: false,
			renderCell: ({ row }: any) => (
				<Tooltip title="Tick to let this person sign in to the help desk with their own Styola email and password">
					<span onClick={(e) => e.stopPropagation()}>
						<Checkbox
							size="small"
							checked={helpDesk.has(String(row.id))}
							disabled={savingId === String(row.id)}
							onChange={(e) => toggleHelpDesk(row, e.target.checked)}
							inputProps={{ "aria-label": `Help-desk access for ${row.name || row.email}` }}
						/>
					</span>
				</Tooltip>
			),
		},
	];

	const getCustomers = async () => {
		try {
			setLoading(true);
			const { data } = await Axios({
				...getCustomerListURL({
					page: paginationModel.page,
					size: paginationModel.pageSize,
					search: debouncedSearch,
				}),
			});

			const responseData = data.data as CustomerData;
			setCustomerList(responseData.data || []);
			setRowCount(responseData.totalElements || 0);
		} catch (error) {
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	// Debounce the search box so a request isn't fired on every keystroke.
	useEffect(() => {
		const t = setTimeout(() => setDebouncedSearch(search), 400);
		return () => clearTimeout(t);
	}, [search]);

	// A new search always starts from the first page.
	useEffect(() => {
		setPaginationModel((p) => ({ ...p, page: 0 }));
	}, [debouncedSearch]);

	useEffect(() => {
		getCustomers();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [paginationModel.page, paginationModel.pageSize, debouncedSearch]);

	const handlePaginationModelChange = (newModel: GridPaginationModel) => {
		setPaginationModel({
			page: newModel.page,
			pageSize: newModel.pageSize,
		});
	};

	const handleRowClick = (params: GridRowParams) => {
		setCustomer(params.row as Customer);
	};

	if (customerDetails) {
		return <CustomerDetails {...{ customerDetails, setCustomer }} />;
	}

	return (
		<>
			<Card className="bg-white rounded-2xl my-6 p-3 md:p-4">
				<FaiBox className={"inventory"}>
					<FaiTypography
						variant={"h4"}
						className="!text-xl !font-semibold"
						mb={2}
					>
						Pending
					</FaiTypography>
				</FaiBox>
				<Box className="grid grid-cols-2 md:grid-cols-4 gap-3">
					<StatisticsCard
						title="New Customer Onboarding"
						number="231"
						change={"3.56"}
					/>
					<StatisticsCard title="Total Review" number="231" change={"3.56"} />
					<StatisticsCard title="Order Place" number="231" change={"3.56"} />
					<StatisticsCard title="Order Returns" number="231" change={"3.56"} />
				</Box>
			</Card>
			<Card className="my-6 p-3 md:p-4">
				<Box>
					<Box sx={{ display: "flex", flexDirection: "column" }}>
						<PageHeader title="Customer" />
					</Box>
					<Box className="flex flex-col md:flex-row justify-between items-center mb-4 w-full gap-3">
						<SearchInput
						placeholder="Search by name, email or mobile"
						value={search}
						onChange={(e: any) => setSearch(e.target.value)}
					/>
					</Box>
					<FaiBox className={"inventory"}>
						<FaiBox className={"inventory-content"}>
							<FaiBox className={"inventory-content-body"} mt={3}>
								{loading ? (
									<CircularProgress />
								) : (
									<FaiDataGrid
										rows={customersList}
										columns={columns}
										pagination={true}
										pageSize={paginationModel.pageSize}
										rowCount={rowCount}
										paginationMode="server"
										onPaginationModelChange={handlePaginationModelChange}
										onRowClick={handleRowClick}
									/>
								)}
							</FaiBox>
						</FaiBox>
					</FaiBox>
				</Box>
			</Card>
		</>
	);
};

export default Customer;
